import { useState, useEffect } from 'react';
import { QuickstartTheme } from '../../../features/quickstart/types/theme';
import { loadDebugMediaStreams, createTestPatternStream } from './debugMedia';
import { gamepadStore } from '@playcastdotio/MobileGamepad';
import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import { Z_DEBUG } from '../../../features/quickstart/config/zIndexLayers';

interface InputEvent {
  type: 'mouse' | 'keyboard' | 'gamepad' | 'xinput';
  timestamp: number;
  data: string;
}

interface ControllerInputEvent {
  type: string;
  timestamp: number;
  eventType: string;
}

interface PeerMessage {
  timestamp: number;
  type: string;
  summary: string;
}

interface PlayerDebugOverlayProps {
  theme?: QuickstartTheme;
  accentColor?: string;
  sessionId?: string;
  extraSessionRows?: Array<{ label: string; value: string }>;
  mobileGamepadEnabled?: boolean;
  onToggleMobileGamepad?: () => void;
  attractOverlayHidden?: boolean;
  onToggleAttractOverlay?: () => void;
  onDebugMediaStream?: (videoStream: MediaStream | null, audioStream: MediaStream | null) => void;
  onMount?: (
    debugMessageCallback: (message: PlaycastMessage<PlaycastMessageTarget>) => void,
    debugControllerInputCallback: (inputType: string, eventType: string) => void
  ) => void;
  onForceIdle?: () => void;
  inGameIdleActive?: boolean;
}

const DEBUG_PANEL_STORAGE_KEY = 'quickstart-debug-panel-collapsed';
const DEBUG_MEDIA_STORAGE_KEY = 'quickstart-debug-media-enabled';

export const PlayerDebugOverlay = ({
  theme,
  accentColor: accentColorProp,
  sessionId,
  extraSessionRows,
  mobileGamepadEnabled = false,
  onToggleMobileGamepad,
  attractOverlayHidden = false,
  onToggleAttractOverlay,
  onDebugMediaStream,
  onMount,
  onForceIdle,
  inGameIdleActive = false,
}: PlayerDebugOverlayProps) => {
  const accentColor = accentColorProp ?? theme?.primaryColor ?? '#A855F7';
  // Load collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(DEBUG_PANEL_STORAGE_KEY);
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const [inputEvents, setInputEvents] = useState<InputEvent[]>([]);
  const [controllerInputEvents, setControllerInputEvents] = useState<ControllerInputEvent[]>([]);
  const [peerMessages, setPeerMessages] = useState<PeerMessage[]>([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [keysPressed, setKeysPressed] = useState<Set<string>>(new Set());
  const [gamepadState, setGamepadState] = useState<{ buttons: boolean[], axes: number[] } | null>(null);

  // Load debug media enabled state from localStorage
  const [debugMediaEnabled, setDebugMediaEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem(DEBUG_MEDIA_STORAGE_KEY);
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [debugMediaLoading, setDebugMediaLoading] = useState(false);
  const [debugMediaCleanup, setDebugMediaCleanup] = useState<(() => void) | null>(null);

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(DEBUG_PANEL_STORAGE_KEY, String(isCollapsed));
    } catch (error) {
      console.warn('[QuickstartPlayerDebugOverlay] Failed to save collapsed state:', error);
    }
  }, [isCollapsed]);

  // Save debug media enabled state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(DEBUG_MEDIA_STORAGE_KEY, String(debugMediaEnabled));
    } catch (error) {
      console.warn('[QuickstartPlayerDebugOverlay] Failed to save debug media state:', error);
    }
  }, [debugMediaEnabled]);

  // Register debug callbacks on mount
  useEffect(() => {
    if (onMount) {
      const handlePeerMessage = (message: PlaycastMessage<PlaycastMessageTarget>) => {
        // Extract meaningful info from the message
        const target = message.header.target;
        const action = message.header.action;
        const messageType = `${target}.${action}`;
        let summary = messageType;

        // Add more context based on message type
        if (action === 'setState' && target === 'gamepad') {
          const gamepadCount = (message.body.message as unknown as { gamepads?: unknown[] })?.gamepads?.length || 0;
          summary = `gamepad.setState (${gamepadCount} pads)`;
        } else if ((action === 'down' || action === 'up') && target === 'keyboard') {
          const key = (message.body.message as unknown as { displayName?: string })?.displayName || '?';
          summary = `keyboard.${action} (${key})`;
        } else if (target === 'mouse') {
          summary = `mouse.${action}`;
        }

        setPeerMessages((prev) => {
          const newMessage = {
            timestamp: Date.now(),
            type: messageType,
            summary,
          };
          return [newMessage, ...prev].slice(0, 50); // Keep last 50 messages
        });
      };

      const handleControllerInput = (inputType: string, eventType: string) => {
        setControllerInputEvents((prev) => {
          const newEvent = {
            type: inputType,
            timestamp: Date.now(),
            eventType,
          };
          return [newEvent, ...prev].slice(0, 30); // Keep last 30 events
        });
      };

      onMount(handlePeerMessage, handleControllerInput);
    }
  }, [onMount]);

  // Auto-load debug media on mount if it was previously enabled
  useEffect(() => {
    const loadSavedMedia = async () => {
      try {
        const saved = localStorage.getItem(DEBUG_MEDIA_STORAGE_KEY);
        if (saved === 'true' && !debugMediaLoading && onDebugMediaStream) {
          console.log('[QuickstartPlayerDebugOverlay] Auto-loading debug media from saved state');
          setDebugMediaLoading(true);

          // Cleanup previous media if it exists
          if (debugMediaCleanup) {
            console.log('[QuickstartPlayerDebugOverlay] Cleaning up previous debug media');
            debugMediaCleanup();
            setDebugMediaCleanup(null);
          }

          const media = await loadDebugMediaStreams();

          if (media.videoStream && media.audioStream) {
            console.log('[QuickstartPlayerDebugOverlay] Debug media auto-loaded successfully');
            onDebugMediaStream(media.videoStream, media.audioStream);
            // Store cleanup function
            setDebugMediaCleanup(() => media.cleanup);
          } else {
            console.warn('[QuickstartPlayerDebugOverlay] Auto-load failed, using test pattern');
            const testPattern = createTestPatternStream();
            onDebugMediaStream(testPattern, null);
            // No cleanup needed for test pattern
            setDebugMediaCleanup(null);
          }

          setDebugMediaLoading(false);
        }
      } catch (error) {
        console.error('[QuickstartPlayerDebugOverlay] Error auto-loading debug media:', error);
        setDebugMediaLoading(false);
      }
    };

    loadSavedMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Cleanup debug media on unmount
  useEffect(() => {
    return () => {
      if (debugMediaCleanup) {
        console.log('[QuickstartPlayerDebugOverlay] Cleaning up debug media on unmount');
        debugMediaCleanup();
      }
    };
  }, [debugMediaCleanup]);

  // Listen for mouse events (using deltas when pointer locked)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Use movement deltas if pointer is locked, otherwise use absolute position
      if (document.pointerLockElement) {
        setMousePos(prev => ({
          x: Math.max(0, Math.min(window.innerWidth, prev.x + e.movementX)),
          y: Math.max(0, Math.min(window.innerHeight, prev.y + e.movementY))
        }));
        addInputEvent('mouse', `Delta: ${e.movementX}, ${e.movementY}`);
      } else {
        setMousePos({ x: e.clientX, y: e.clientY });
        addInputEvent('mouse', `Move: ${e.clientX}, ${e.clientY}`);
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      addInputEvent('mouse', `Down: Button ${e.button}`);
    };

    const handleMouseUp = (e: MouseEvent) => {
      addInputEvent('mouse', `Up: Button ${e.button}`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Reset mouse position when pointer lock changes
  useEffect(() => {
    const handlePointerLockChange = () => {
      if (document.pointerLockElement) {
        // Center the virtual cursor when entering pointer lock
        setMousePos({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
      }
    };

    document.addEventListener('pointerlockchange', handlePointerLockChange);
    return () => document.removeEventListener('pointerlockchange', handlePointerLockChange);
  }, []);

  // Listen for keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      setKeysPressed(prev => new Set(prev).add(e.key));
      addInputEvent('keyboard', `Down: ${e.key}`);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      setKeysPressed(prev => {
        const next = new Set(prev);
        next.delete(e.key);
        return next;
      });
      addInputEvent('keyboard', `Up: ${e.key}`);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Subscribe to mobile gamepad XInput state
  useEffect(() => {
    const unsubscribe = gamepadStore.subscribeGamepad((state) => {
      // Log button changes
      if (state.wButtons !== 0) {
        const buttonNames: string[] = [];
        if (state.wButtons & 0x1000) buttonNames.push('A');
        if (state.wButtons & 0x2000) buttonNames.push('B');
        if (state.wButtons & 0x4000) buttonNames.push('X');
        if (state.wButtons & 0x8000) buttonNames.push('Y');
        if (state.wButtons & 0x0100) buttonNames.push('LB');
        if (state.wButtons & 0x0200) buttonNames.push('RB');
        if (state.wButtons & 0x0001) buttonNames.push('DUp');
        if (state.wButtons & 0x0002) buttonNames.push('DDown');
        if (state.wButtons & 0x0004) buttonNames.push('DLeft');
        if (state.wButtons & 0x0008) buttonNames.push('DRight');
        if (state.wButtons & 0x0010) buttonNames.push('Start');
        if (state.wButtons & 0x0020) buttonNames.push('Back');
        if (state.wButtons & 0x0040) buttonNames.push('L3');
        if (state.wButtons & 0x0080) buttonNames.push('R3');

        if (buttonNames.length > 0) {
          addInputEvent('xinput', `Buttons: ${buttonNames.join(', ')}`);
        }
      }

      // Log stick movements (only if significant - normalize from -32768 to 32767 range)
      const lx = state.sThumbLX / 32768;
      const ly = state.sThumbLY / 32768;
      const rx = state.sThumbRX / 32768;
      const ry = state.sThumbRY / 32768;

      if (Math.abs(lx) > 0.1 || Math.abs(ly) > 0.1) {
        addInputEvent('xinput', `L-Stick: ${lx.toFixed(2)}, ${ly.toFixed(2)}`);
      }
      if (Math.abs(rx) > 0.1 || Math.abs(ry) > 0.1) {
        addInputEvent('xinput', `R-Stick: ${rx.toFixed(2)}, ${ry.toFixed(2)}`);
      }

      // Log trigger changes (only if pressed - normalize from 0-255 range)
      const lt = state.bLeftTrigger / 255;
      const rt = state.bRightTrigger / 255;

      if (lt > 0.1) {
        addInputEvent('xinput', `LT: ${lt.toFixed(2)}`);
      }
      if (rt > 0.1) {
        addInputEvent('xinput', `RT: ${rt.toFixed(2)}`);
      }
    });

    return unsubscribe;
  }, []);

  // Poll for gamepad state
  useEffect(() => {
    const pollGamepad = () => {
      const gamepads = navigator.getGamepads();
      const gp = gamepads[0];

      if (gp) {
        const buttons = gp.buttons.map(b => b.pressed);
        const axes = Array.from(gp.axes);

        setGamepadState({ buttons, axes });

        // Log button presses
        buttons.forEach((pressed, i) => {
          if (pressed) {
            addInputEvent('gamepad', `Button ${i} pressed`);
          }
        });
      } else {
        setGamepadState(null);
      }
    };

    const interval = setInterval(pollGamepad, 100);
    return () => clearInterval(interval);
  }, []);

  const addInputEvent = (type: InputEvent['type'], data: string) => {
    setInputEvents(prev => {
      const newEvent = { type, timestamp: Date.now(), data };
      return [newEvent, ...prev].slice(0, 20); // Keep last 20 events
    });
  };

  // Handle debug media toggle
  const handleToggleDebugMedia = async () => {
    if (debugMediaEnabled) {
      // Turn off - clear the streams and cleanup
      console.log('[QuickstartPlayerDebugOverlay] Disabling debug media');

      // Cleanup the debug media elements
      if (debugMediaCleanup) {
        console.log('[QuickstartPlayerDebugOverlay] Calling debug media cleanup');
        debugMediaCleanup();
        setDebugMediaCleanup(null);
      }

      setDebugMediaEnabled(false);
      if (onDebugMediaStream) {
        onDebugMediaStream(null, null);
      }
    } else {
      // Turn on - load the streams
      console.log('[QuickstartPlayerDebugOverlay] Loading debug media');
      setDebugMediaLoading(true);

      // Cleanup previous media if it exists (shouldn't happen but be safe)
      if (debugMediaCleanup) {
        console.log('[QuickstartPlayerDebugOverlay] Cleaning up previous debug media before loading new');
        debugMediaCleanup();
        setDebugMediaCleanup(null);
      }

      try {
        const media = await loadDebugMediaStreams();

        if (media.videoStream && media.audioStream && onDebugMediaStream) {
          console.log('[QuickstartPlayerDebugOverlay] Debug media loaded successfully');
          onDebugMediaStream(media.videoStream, media.audioStream);
          // Store cleanup function
          setDebugMediaCleanup(() => media.cleanup);
          setDebugMediaEnabled(true);
        } else {
          console.warn('[QuickstartPlayerDebugOverlay] Failed to load debug media, using test pattern');
          const testPattern = createTestPatternStream();
          if (onDebugMediaStream) {
            onDebugMediaStream(testPattern, null); // Test pattern is video-only
          }
          // No cleanup needed for test pattern
          setDebugMediaCleanup(null);
          setDebugMediaEnabled(true);
        }
      } catch (error) {
        console.error('[QuickstartPlayerDebugOverlay] Error loading debug media:', error);
        // Fallback to test pattern
        const testPattern = createTestPatternStream();
        if (onDebugMediaStream) {
          onDebugMediaStream(testPattern, null); // Test pattern is video-only
        }
        // No cleanup needed for test pattern
        setDebugMediaCleanup(null);
        setDebugMediaEnabled(true);
      } finally {
        setDebugMediaLoading(false);
      }
    }
  };

  if (isCollapsed) {
    return (
      <div className='fixed left-0 top-0' style={{ zIndex: Z_DEBUG }}>
        <button
          onClick={() => setIsCollapsed(false)}
          className='rounded-br-lg bg-purple-600 px-3 py-2 font-mono text-xs font-bold text-white shadow-lg hover:bg-purple-700'
        >
          Debug ▼
        </button>
      </div>
    );
  }

  return (
    <div className='pointer-events-none fixed inset-0' style={{ zIndex: Z_DEBUG }}>
      {/* Virtual Mouse Cursor */}
      <div
        className='absolute h-4 w-4 rounded-full bg-red-500 opacity-50'
        style={{
          left: mousePos.x - 8,
          top: mousePos.y - 8,
          pointerEvents: 'none',
        }}
      />

      {/* Debug Panel - Top Left */}
      <div className='pointer-events-auto absolute left-0 top-0 max-h-screen w-96 overflow-y-auto bg-gray-900/95 p-4 font-mono text-xs text-white shadow-2xl'>
        {/* Header */}
        <div className='mb-4 flex items-center justify-between'>
          <h3 className='font-bold' style={{ color: accentColor }}>
            Player Debug
          </h3>
          <button
            onClick={() => setIsCollapsed(true)}
            className='rounded bg-gray-700 px-2 py-1 hover:bg-gray-600'
          >
            ▲
          </button>
        </div>

        {/* Session Info */}
        {(sessionId || extraSessionRows) && (
          <div className='mb-4 rounded border border-purple-600/50 bg-purple-900/30 p-2'>
            {sessionId && (
              <>
                <div className='mb-1 flex items-center justify-between'>
                  <p className='font-semibold text-purple-200'>Session ID</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(sessionId);
                    }}
                    className='rounded bg-purple-700/50 px-2 py-0.5 text-purple-100 hover:bg-purple-600/50'
                    title='Copy to clipboard'
                  >
                    Copy
                  </button>
                </div>
                <p className='break-all text-purple-300'>{sessionId}</p>
              </>
            )}
            {extraSessionRows?.map((row) => (
              <div key={row.label} className='mt-1 flex justify-between text-purple-300'>
                <span className='text-purple-400'>{row.label}</span>
                <span className='font-mono'>{row.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Debug Controls */}
        <div className='mb-4 rounded border border-gray-700 bg-gray-800 p-3'>
          <h4 className='mb-2 font-semibold text-cyan-400'>Test Controls</h4>

          {/* Debug Media Toggle */}
          <div className='mb-2 flex items-center justify-between'>
            <span className='text-gray-300'>Test Media</span>
            <button
              onClick={handleToggleDebugMedia}
              disabled={debugMediaLoading}
              className={`rounded px-3 py-1 font-semibold transition-colors ${
                debugMediaEnabled
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-600 hover:bg-gray-700'
              } ${debugMediaLoading ? 'cursor-wait opacity-50' : ''}`}
            >
              {debugMediaLoading ? 'Loading...' : debugMediaEnabled ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* Mobile Gamepad Toggle */}
          {onToggleMobileGamepad && (
          <div className='mb-2 flex items-center justify-between'>
            <span className='text-gray-300'>Mobile Gamepad</span>
            <button
              onClick={onToggleMobileGamepad}
              className={`rounded px-3 py-1 font-semibold transition-colors ${
                mobileGamepadEnabled
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
            >
              {mobileGamepadEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
          )}

          {/* Attract Overlay Toggle */}
          {onToggleAttractOverlay && (
          <div className='mb-2 flex items-center justify-between'>
            <span className='text-gray-300'>Attract Overlay</span>
            <button
              onClick={onToggleAttractOverlay}
              className={`rounded px-3 py-1 font-semibold transition-colors ${
                attractOverlayHidden
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {attractOverlayHidden ? 'HIDDEN' : 'VISIBLE'}
            </button>
          </div>
          )}

          {/* Simulate Idle / AFK Toggle */}
          {onForceIdle && (
            <div className='mb-2 flex items-center justify-between'>
              <span className='text-gray-300'>Simulate Idle</span>
              <button
                onClick={onForceIdle}
                className={`rounded px-3 py-1 font-semibold transition-colors ${
                  inGameIdleActive
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {inGameIdleActive ? 'ACTIVE' : 'TRIGGER'}
              </button>
            </div>
          )}

          {/* <div className='mt-2 text-[10px] text-gray-400'>
            <p className='mb-1'>✓ Test Media: Video (Big Buck Bunny) + Audio (Sea Shanty 2)</p>
            <p className='mb-1'>✓ Mouse: Move/Click to test</p>
            <p className='mb-1'>✓ Keyboard: Press any key to test</p>
            <p className='mb-1'>✓ Gamepad: Connect physical gamepad</p>
            <p>✓ Mobile: Toggle virtual gamepad above</p>
          </div> */}
        </div>

        {/* Mouse State */}
        <div className='mb-4'>
          <h4 className='mb-1 font-semibold text-blue-400'>Mouse</h4>
          <p>Position: {mousePos.x}, {mousePos.y}</p>
          <p>Pointer Lock: {document.pointerLockElement ? 'Yes' : 'No'}</p>
        </div>

        {/* Keyboard State */}
        <div className='mb-4'>
          <h4 className='mb-1 font-semibold text-green-400'>Keyboard</h4>
          <p>Keys Pressed: {keysPressed.size > 0 ? Array.from(keysPressed).join(', ') : 'None'}</p>
          <p>Keyboard Lock: {document.pointerLockElement ? 'Yes' : 'No'}</p>
        </div>

        {/* Gamepad State */}
        <div className='mb-4'>
          <h4 className='mb-1 font-semibold text-purple-400'>Gamepad</h4>
          {gamepadState ? (
            <>
              <p>Connected: Yes</p>
              <p>Buttons: {gamepadState.buttons.filter(Boolean).length} pressed</p>
              <p>Axes: {gamepadState.axes.map(a => a.toFixed(2)).join(', ')}</p>
            </>
          ) : (
            <p>Connected: No</p>
          )}
        </div>

        {/* Fullscreen State */}
        <div className='mb-4'>
          <h4 className='mb-1 font-semibold text-yellow-400'>Display</h4>
          <p>Fullscreen: {document.fullscreenElement ? 'Yes' : 'No'}</p>
        </div>

        {/* Controller Captured Events */}
        <div className='mb-4'>
          <h4 className='mb-1 font-semibold text-cyan-400'>Controller Captured Events (last 30)</h4>
          <div className='max-h-48 space-y-1 overflow-y-auto rounded bg-black/50 p-2'>
            {controllerInputEvents.length === 0 && (
              <p className='text-gray-500'>No events captured by controller yet</p>
            )}
            {controllerInputEvents.map((event, i) => (
              <div key={i} className='text-[10px]'>
                <span className={
                  event.type === 'mouse' ? 'text-blue-400' :
                  event.type === 'keyboard' ? 'text-green-400' :
                  event.type === 'touch' ? 'text-yellow-400' :
                  'text-purple-400'
                }>
                  [{event.type}]
                </span>
                {' '}{event.eventType}
              </div>
            ))}
          </div>
        </div>

        {/* Input Event Log */}
        <div className='mb-4'>
          <h4 className='mb-1 font-semibold text-orange-400'>Local Input Events (last 20)</h4>
          <div className='max-h-48 space-y-1 overflow-y-auto rounded bg-black/50 p-2'>
            {inputEvents.length === 0 && (
              <p className='text-gray-500'>No events yet</p>
            )}
            {inputEvents.map((event, i) => (
              <div key={i} className='text-[10px]'>
                <span className={
                  event.type === 'mouse' ? 'text-blue-400' :
                  event.type === 'keyboard' ? 'text-green-400' :
                  'text-purple-400'
                }>
                  [{event.type}]
                </span>
                {' '}{event.data}
              </div>
            ))}
          </div>
        </div>

        {/* WebRTC Messages to Peer */}
        <div className='mb-4'>
          <h4 className='mb-1 font-semibold text-pink-400'>WebRTC Messages to Peer (last 50)</h4>
          <div className='max-h-48 space-y-1 overflow-y-auto rounded bg-black/50 p-2'>
            {peerMessages.length === 0 && (
              <p className='text-gray-500'>No messages sent yet</p>
            )}
            {peerMessages.map((msg, i) => {
              const timeSinceMessage = Date.now() - msg.timestamp;
              const isRecent = timeSinceMessage < 1000; // Highlight if within 1 second

              return (
                <div
                  key={i}
                  className={`text-[10px] ${isRecent ? 'bg-pink-900/30 rounded px-1' : ''}`}
                >
                  <span className='text-pink-400'>
                    [{new Date(msg.timestamp).toLocaleTimeString('en-US', {
                      hour12: false,
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      fractionalSecondDigits: 3
                    })}]
                  </span>
                  {' '}
                  <span className='text-pink-300'>{msg.summary}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Instructions */}
        <div className='mt-4 rounded border border-gray-700 bg-gray-800 p-2 text-[10px]'>
          <p className='mb-1 font-semibold'>Debug Mode Active</p>
          <p className='text-gray-400'>
            Test all input methods without WebRTC connection. Red cursor shows virtual mouse position.
            Toggle mobile gamepad to test touch controls on desktop. WebRTC messages show what would be sent to peer.
          </p>
        </div>
      </div>
    </div>
  );
};

/** @deprecated Use PlayerDebugOverlay instead */
export const QuickstartPlayerDebugOverlay = PlayerDebugOverlay;

